import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useDirty } from "@/plugins/common/composables/page-dirty";
import { useToast } from "@/plugins/toast/composables/toast-instance";
import { useModal } from "@/plugins/modal/composables/modal-instance";
import { isNull } from "@/utils/var.check";
import { ERROR_CODE, NAMESPACE } from "../service/attempt.constant";
import { getAttemptErrorCode } from "../service/attempt.utils";
import axios from "axios";

interface ModalProps {
  title: string;
  content: string;
  primaryBtnLabel: string | null;
  use_secondaryButton: boolean;
  showClose: boolean;
  actionPrimary: () => void;
  actionHidden: () => void;
  actionHideRequest: () => void;
}

export function useAttemptError() {
  const store = useStore();
  const { t } = useI18n();
  const router = useRouter();
  const modal = useModal();
  const toast = useToast();
  const { clearDirty } = useDirty(ref(""));

  const getPasscodeRoute = computed(() => store.getters[`${NAMESPACE.ATTEMPT}/getPasscodeRoute`]);
  const currentRouteName = computed(() => store.getters[`${NAMESPACE.APP}/currentRouteName`]);

  const setPaused = (value) => store.dispatch(`${NAMESPACE.ATTEMPT}/setPaused`, value);

  const showErrorModal = (
    title: string,
    content: string,
    primaryBtnLabel: null | string = null,
    showClose = true,
  ): Promise<void> => {
    return new Promise((resolve) => {
      const props: ModalProps = {
        title,
        content,
        primaryBtnLabel: !isNull(primaryBtnLabel) ? primaryBtnLabel : t("button_ok"),
        use_secondaryButton: false,
        showClose,
        actionPrimary: resolve,
        actionHidden: resolve,
        actionHideRequest: resolve,
      };
      modal.showMessage("warning", props);
    });
  };

  const showErrorToast = (message: string) => {
    toast.show("error", {
      kind: "error",
      title: message,
    });
  };

  const showAssignmentError = async (error: unknown) => {
    if (
      axios.isAxiosError(error) &&
      (error.code === "ERR_NETWORK" || error.message === "Network Error")
    ) {
      return;
    }

    const errorCode = getAttemptErrorCode(error);

    switch (errorCode) {
      case ERROR_CODE.ASSIGNMENT_NOT_FOUND:
      case ERROR_CODE.LESSON_NOT_FOUND:
        modal.removeAll();
        router.push({ name: "PageNotFound" });
        break;

      case ERROR_CODE.CURRENT_USER_ACCESS_DENIED:
      case ERROR_CODE.ASSIGNMENT_ACCESS_DENIED:
        await showErrorModal(
          t("assignment_error_modal_title"),
          t("assignment_view_no_access_unassigned"),
        );
        modal.removeAll();
        router.push({ name: "Dashboard" });
        break;

      case ERROR_CODE.ASSIGNMENT_PAUSED:
        setPaused(true);
        break;

      case ERROR_CODE.ASSIGNMENT_SHARING_RESOURCE_ACCESS_DENIED:
        await showErrorModal(
          t("assignment_error_modal_title"),
          t("assignment_view_assignment_sharing_resource_access_denied"),
        );
        break;

      case ERROR_CODE.OTHER_USER_ACCESS_DENIED:
        await showErrorModal(
          t("assignment_error_modal_title"),
          t("assignment_view_other_student_unassigned"),
        );
        break;

      case ERROR_CODE.PASSCODE_NOT_AUTHENTICATED:
        if (currentRouteName.value === getPasscodeRoute.value) {
          return;
        }
        await showErrorModal(
          t("passcode_modal_no_access_title"),
          t("passcode_modal_no_access_subtitle"),
          t("passcode_modal_no_access_button_label"),
          false,
        );
        modal.removeAll();
        clearDirty();
        router.push({ name: getPasscodeRoute.value });
        break;

      case ERROR_CODE.ACCESS_DENIED:
        modal.removeAll();
        router.push({ name: "Forbidden" });
        break;

      default:
        await showErrorModal(
          t("assignment_error_modal_title"),
          t("api_action_generic_error_message"),
        );
        break;
    }
  };

  const showLessonError = (errorCode: string) => {
    switch (errorCode) {
      case ERROR_CODE.ACCESS_DENIED:
        router.push({ name: "Forbidden" });
        break;

      case ERROR_CODE.LESSON_NOT_FOUND:
        router.push({ name: "PageNotFound" });
        break;

      default:
        showErrorToast(t("api_action_generic_error_message"));
        break;
    }
  };

  return {
    getPasscodeRoute,
    currentRouteName,
    setPaused,
    showAssignmentError,
    showLessonError,
    showErrorModal,
    showErrorToast,
  };
}
